import {useCallback, useRef, type KeyboardEvent} from 'react';
import type {SwiperClass} from 'swiper/react';
import {useProductGallery} from '../../../../ProductGalleryProvider/ProductGalleryProvider';
import {ArrowDirection, SWIPER_SPEED} from '../constants';
import {useEnvironment} from '@wix/yoshi-flow-editor';

export const useSwiperNavigation = (mediaLength: number, swiper: SwiperClass, isVertical: boolean) => {
  const {selectedIndex, changeSelectedIndex} = useProductGallery();
  const {isRTL} = useEnvironment();
  const navigationLockRef = useRef(false);

  const unlockNavigation = () => {
    navigationLockRef.current = false;
  };

  const blockNavigation = useCallback(() => {
    navigationLockRef.current = true;
    setTimeout(unlockNavigation, SWIPER_SPEED);
  }, []);

  const navigate = useCallback(
    (isPrev: boolean) => {
      const isNavigationBlocked = navigationLockRef.current;
      if (isNavigationBlocked) {
        return;
      }
      let newIndex = selectedIndex;
      if (isPrev) {
        newIndex = selectedIndex === 0 ? mediaLength - 1 : selectedIndex - 1;
      } else {
        newIndex = selectedIndex === mediaLength - 1 ? 0 : selectedIndex + 1;
      }
      swiper?.slideToLoop(newIndex);
      changeSelectedIndex(newIndex);
      blockNavigation();
    },
    [selectedIndex, mediaLength, changeSelectedIndex, blockNavigation, swiper]
  );

  const prevNavigation = useCallback(() => navigate(true), [navigate]);
  const nextNavigation = useCallback(() => navigate(false), [navigate]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.stopPropagation();

      if (isVertical) {
        switch (e.key) {
          case ArrowDirection.UP:
            e.preventDefault();
            prevNavigation();
            break;
          case ArrowDirection.DOWN:
            e.preventDefault();
            nextNavigation();
            break;
        }
      } else {
        switch (e.key) {
          case ArrowDirection.UP:
            e.preventDefault();
            nextNavigation();
            break;
          case ArrowDirection.DOWN:
            e.preventDefault();
            prevNavigation();
            break;
          case ArrowDirection.LEFT:
            e.preventDefault();
            isRTL ? nextNavigation() : prevNavigation();
            break;
          case ArrowDirection.RIGHT:
            e.preventDefault();
            isRTL ? prevNavigation() : nextNavigation();
            break;
        }
      }
    },
    [isVertical, isRTL, prevNavigation, nextNavigation]
  );

  return {
    prevNavigation,
    nextNavigation,
    handleKeyDown,
  };
};
